import React from "react";
import { Alert } from "antd5";
import { DateTime } from "luxon";
import { Flex } from "styles/utility-components";

import Button from "components/ui/button/Button";
import UiEmptyState from "components/ui/empty_state/EmptyState";
import { BetaIcon } from "lib/core_components/Labels";
import { countryCodes } from "lib/data/optionItems";
import { SpendFilters } from "lib/hooks/api/spend/spendFilters";
import { useRegions } from "lib/hooks/api/useRegions";
import { isTimeoutError } from "lib/queryRetry";

/**
 * Shown in place of a spend chart when its query fails (incl. the 25s backend
 * timeout / 30s client timeout) so the user gets a message + retry instead of a
 * spinner that never resolves.
 */
export function SpendChartError({
  error,
  onRetry,
  subject = "spend data",
}: {
  error: unknown;
  onRetry: () => void;
  /** What failed, for a view that isn't spend alone, e.g. "this chart". */
  subject?: string;
}) {
  const description = isTimeoutError(error)
    ? `Loading ${subject} took too long. Try narrowing the date range or filters, then try again.`
    : `Something went wrong loading ${subject}. Please try again.`;
  return (
    <Alert
      type="error"
      showIcon
      message={`Couldn't load ${subject}`}
      description={description}
      action={
        <Button buttonType="secondary" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

function monthYear(isoDate: string): string {
  return DateTime.fromISO(isoDate).toFormat("LLL yyyy", { locale: "en-GB" });
}

function dateRangePhrase(from?: string | null, to?: string | null): string {
  if (from && to) {
    return ` between ${monthYear(from)} and ${monthYear(to)}`;
  }
  if (from) {
    return ` since ${monthYear(from)}`;
  }
  if (to) {
    return ` up to ${monthYear(to)}`;
  }
  return "";
}

function listOfNames(names: string[]): string {
  if (names.length === 1) {
    return names[0];
  }
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * With a buyer location set, names the place and dates, as a location is the filter most
 * likely to empty the page. Undefined without one, or until the region names have loaded.
 */
function useLocationHeading(filters?: SpendFilters): string | undefined {
  const countries = filters?.buyerCountryCodes ?? [];
  const regionIds = filters?.buyerRegionIds ?? [];
  const hasLocation = countries.length > 0 || regionIds.length > 0;
  const { data: allRegions } = useRegions({ enabled: hasLocation });

  if (!hasLocation) {
    return undefined;
  }
  const names = [
    ...countries.map((code) => countryCodes.find((country) => country.code === code)?.name),
    ...regionIds.map((id) => allRegions?.regions.find((region) => region.id === id)?.name),
  ].filter((name): name is string => name !== undefined);
  if (names.length !== countries.length + regionIds.length) {
    return undefined;
  }

  return `No spend from buyers in ${listOfNames(names)}${dateRangePhrase(filters?.dateFrom, filters?.dateTo)}.`;
}

/** A search ran and matched nothing. Distinct from the starting state, where nothing has been searched. */
export function EmptyState({ filters }: { filters?: SpendFilters }) {
  const locationHeading = useLocationHeading(filters);

  return (
    <Flex column alignItems="center" gap={24} width="100%">
      <UiEmptyState
        size="large"
        type="noResults"
        width="100%"
        heading={locationHeading ?? "No results match your current filters"}
        description="Try removing or adjusting your filters to widen your search."
      />
      <Alert
        message={<BetaIcon dark />}
        description={
          <span>
            We are currently still collecting data for some buyers which could be impacting your
            search. Right now we only cover Central Gov as well as a portion of Local Gov.
          </span>
        }
        type="warning"
        style={{ maxWidth: 480 }}
      />
    </Flex>
  );
}
